import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// Parse "January 2027" -> {month:"January", year:"2027"}
export const parseDeferredTo = (s) => {
  if (!s || typeof s !== "string") return { month: "", year: "" };
  const parts = s.trim().split(/\s+/);
  if (parts.length !== 2) return { month: "", year: "" };
  return { month: MONTHS.includes(parts[0]) ? parts[0] : "", year: /^\d{4}$/.test(parts[1]) ? parts[1] : "" };
};

/**
 * Modal to collect / edit Deferred Month + Year for an application.
 * onConfirm receives a single string "Month YYYY" (e.g. "January 2027").
 */
export default function DeferredDialog({ open, onOpenChange, initialValue = "", onConfirm, saving = false }) {
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  useEffect(() => {
    if (open) {
      const p = parseDeferredTo(initialValue);
      setMonth(p.month);
      setYear(p.year);
    }
  }, [open, initialValue]);

  const canSave = MONTHS.includes(month) && /^\d{4}$/.test(year);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="deferred-dialog">
        <DialogHeader>
          <DialogTitle>Defer this application</DialogTitle>
          <DialogDescription>Select the intake month and year the student is deferring to. Both fields are required.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Deferred Month *</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="deferred-month-select"><SelectValue placeholder="Select month"/></SelectTrigger>
              <SelectContent>{MONTHS.map(m => <SelectItem key={m} value={m} data-testid={`deferred-month-${m}`}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Deferred Year *</Label>
            <Input
              data-testid="deferred-year-input"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              value={year}
              placeholder="e.g. 2027"
              onChange={(e) => setYear(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
            />
          </div>
          {canSave && (
            <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
              Preview: <span className="font-semibold">Deferred to {month} {year}</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            data-testid="deferred-confirm-btn"
            disabled={!canSave || saving}
            onClick={() => onConfirm(`${month} ${year}`)}
            className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save & Defer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
